import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/api-client";
import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

export type RunMode = "voice" | "text";
export type RunStatus = "queued" | "running" | "completed" | "failed" | "cancelled";
export type CellStatus = "pending" | "running" | "scored" | "failed";
export type MetricKind = "llm_judge" | "deterministic" | "statistical";
export type MetricMode = "both" | "voice_only";

export interface Scenario {
	id: string;
	name: string;
	situation: string;
	contact: Record<string, any>;
	contactPhone: string | null;
	/** The agent this was written for. Null means it suits any of them. */
	agentId: string | null;
	agentName: string | null;
}

export interface Metric {
	id: string;
	name: string;
	description: string | null;
	kind: MetricKind;
	mode: MetricMode;
	definition: string;
	config: Record<string, any>;
	scenarioIds: string[];
}

export interface RunSummary {
	id: number;
	agentId: string;
	agentName: string;
	mode: RunMode;
	status: RunStatus;
	totalCells: number;
	completedCells: number;
	passedChecks: number;
	totalChecks: number;
	durationMs: number;
	createdAt: string;
	finishedAt: string | null;
	error: string | null;
	/**
	 * Usage and cost. All optional: the server omits them entirely for a reader who may not
	 * see what a run cost, so `undefined` means "not yours to see" and `null` on spendUsd
	 * means "nothing is priced". Typing these as required is what let a manager's list crash
	 * on `undefined.toFixed`.
	 */
	inputTokens?: number;
	outputTokens?: number;
	/** Characters handed to speech synthesis. What ElevenLabs meters. */
	ttsCharacters?: number;
	/** Seconds of open transcription connection, both ears. What Deepgram meters. */
	sttSeconds?: number;
	/** Null when nothing is priced — distinct from a run that genuinely cost nothing. */
	spendUsd?: number | null;
	/** Which meters the figure covers, e.g. ["models","speech"]. */
	spendCovers?: string[];
	/**
	 * How long the agent took to answer, over every turn of every call in the run. Null when
	 * nothing was measured — a text run, or one too short to contain an exchange.
	 */
	latencyP50Ms: number | null;
	latencyP95Ms: number | null;
	latencyMaxMs: number | null;
	/** Turns the figures are drawn from. A p95 over three turns is a weak claim. */
	latencySamples: number;
	/** When an owner published this run to the team. Null means only owners can see it. */
	publishedAt: string | null;
}

export interface CellVerdictSummary {
	metricId: string;
	passed: boolean | null;
	notApplicable: boolean;
	overridden: boolean;
}

export interface RunCellSummary {
	id: number;
	scenarioId: string;
	personaId: string;
	status: CellStatus;
	verdicts: CellVerdictSummary[];
	passedChecks: number;
	totalChecks: number;
	durationMs: number;
	turns: number;
	error: string | null;
}

export interface Suggestion {
	title: string;
	severity: "high" | "medium" | "low";
	problem: string;
	/** Quoted from the transcripts — the reason to believe the problem is real. */
	evidence: string[];
	/** Names of the checks this explains, tying a suggestion back to a number. */
	metrics: string[];
	/** Text already in the prompt that should change. Empty when the edit adds something. */
	before: string;
	after: string;
	where: string;
	/** Set once this edit has been written to the agent. */
	appliedAt?: string;
}

export interface RunDetail extends RunSummary {
	scenarios: { id: string; name: string }[];
	personas: { id: string; name: string }[];
	metrics: { id: string; name: string; kind: MetricKind }[];
	cells: RunCellSummary[];
	/** Edits to the agent's prompt, argued from this run's failures. Absent for a manager. */
	suggestions?: Suggestion[];
	/** Always sent — null while advice is still being written, or when there is none. */
	suggestionsAt: string | null;
	/** Stated by the server rather than inferred, so the screen matches what the API allows. */
	canPublish?: boolean;
	canEdit?: boolean;
	/** The agent's prompt as it stood when the run started. Absent for a manager. */
	agentPrompt?: string;
}

export interface CellVerdict {
	id: number;
	metricId: string;
	metricName: string;
	kind: MetricKind;
	passed: boolean | null;
	reasoning: string | null;
	value: number | null;
	notApplicable: boolean;
	humanPassed: boolean | null;
	humanNote: string | null;
	humanAt: string | null;
	/** A person contradicted the judge. The only evidence of whether the judge is any good. */
	disagrees: boolean;
}

export interface CellDetail {
	id: number;
	runId: number;
	scenarioName: string;
	personaName: string;
	status: CellStatus;
	transcript: { speaker: string; content: string; atMs: number }[];
	endedBy: string | null;
	hangUpReason: string | null;
	durationMs: number;
	turns: number;
	agentTalkRatio: number | null;
	/** How long the agent took to answer on this call. Null in text mode. */
	latencyP50Ms: number | null;
	latencyP95Ms: number | null;
	latencyMaxMs: number | null;
	latencySamples: number;
	hasRecording: boolean;
	/** The agent's conversation for this call. Null for cells recorded before it was stored. */
	conversationId: number | null;
	/** English rendering of each turn, keyed by turn id. Empty until somebody asks for one. */
	transcriptTranslation: Record<string, string>;
	error: string | null;
	results: CellVerdict[];
}

export interface ProvingGroundSettings {
	judgePluginId: string | null;
	judgeSettings: Record<string, any>;
	defaultPersonaServices: Record<string, { pluginId?: string; settings?: Record<string, any> }>;
	defaultPersonaLanguages: string[];
	costRates: {
		llm?: { in?: number; out?: number };
		tts?: { perMillionChars?: number };
		stt?: { perMinute?: number };
	};
	maxCallSeconds: number;
	concurrency: number;
}

/* ------------------------------------------------------------- bulk drafting */

/**
 * Drafting and saving are separate calls. Generating a dozen callers straight into the
 * library gives you a dozen to weed, and a bad one is indistinguishable from a good one
 * until a run has already used it.
 */
export type DraftKind = "personas" | "scenarios" | "metrics";

export function useGenerateDrafts(kind: DraftKind) {
	return useMutation({
		/** `agentId` derives from that agent's own prompt; `instruction` then only steers. */
		mutationFn: async ({
			instruction,
			count,
			agentId,
		}: {
			instruction: string;
			count: number;
			agentId?: string;
		}) =>
			await apiClient
				.post(`/proving-ground/generate/${kind}`, { instruction, count, ...(agentId ? { agentId } : {}) })
				.then((response) => response.data as Record<string, any>[]),
		onError: (error) => reportError(error, "Could not draft those"),
	});
}

export function useAcceptDrafts(kind: DraftKind) {
	const queryClient = useQueryClient();
	return useMutation({
		/** The agent goes with them: the tag is decided at generation, applied at save. */
		mutationFn: async ({ drafts, agentId }: { drafts: Record<string, any>[]; agentId?: string }) =>
			await apiClient
				.post(`/proving-ground/generate/${kind}/accept`, { drafts, ...(agentId ? { agentId } : {}) })
				.then((response) => response.data as { created: number }),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.all });
			queryClient.invalidateQueries({ queryKey: ["personas"] });
			toast.success(`Created ${result.created}`);
		},
		onError: (error) => reportError(error, "Could not save those"),
	});
}

export function usePublishRun() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, published }: { id: number; published: boolean }) =>
			await (published
				? apiClient.post(`/proving-ground/runs/${id}/publish`)
				: apiClient.delete(`/proving-ground/runs/${id}/publish`)
			).then((response) => response.data as { publishedAt: string | null }),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.all });
			toast.success(result.publishedAt ? "Published to the team" : "Taken out of view");
		},
		onError: (error) => reportError(error, "Could not change who can see this run"),
	});
}

/**
 * Writes one suggestion into the agent's prompt.
 *
 * `before` and `after` are sent only when the operator edited them, so applying an untouched
 * suggestion cannot drift from what was reviewed.
 */
export function useApplySuggestion(runId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ index, before, after }: { index: number; before?: string; after?: string }) =>
			await apiClient
				.post(`/proving-ground/runs/${runId}/suggestions/${index}/apply`, { before, after })
				.then((response) => response.data as { applied: boolean; agentId: string }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.run(runId) });
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			toast.success("Prompt updated");
		},
		onError: (error) => reportError(error, "Could not apply that edit"),
	});
}

/**
 * Translates a call into English, once.
 *
 * The result is stored on the cell, so this is cheap on every request after the first and the
 * toggle costs nothing to flip back and forth.
 */
export function useTranslateCell(cellId?: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () =>
			await apiClient
				.post(`/proving-ground/runs/cells/${cellId}/translate`)
				.then((response) => response.data as { translation: Record<string, string> }),
		onSuccess: () => {
			if (cellId) queryClient.invalidateQueries({ queryKey: provingGroundKeys.cell(cellId) });
		},
		onError: (error) => reportError(error, "Could not translate this call"),
	});
}

export const provingGroundKeys = {
	all: ["proving-ground"] as const,
	runs: () => [...provingGroundKeys.all, "runs"] as const,
	run: (id: number) => [...provingGroundKeys.runs(), id] as const,
	cell: (id: number) => [...provingGroundKeys.all, "cell", id] as const,
	scenarios: () => [...provingGroundKeys.all, "scenarios"] as const,
	metrics: () => [...provingGroundKeys.all, "metrics"] as const,
	settings: () => [...provingGroundKeys.all, "settings"] as const,
};

function reportError(error: any, fallback: string) {
	logger.error(error);
	const message = error?.response?.data?.message;
	toast.error(Array.isArray(message) ? message[0] : message || fallback);
}

/* ---------------------------------------------------------------- scenarios */

export function useScenarios() {
	return useQuery<Scenario[]>({
		queryKey: provingGroundKeys.scenarios(),
		queryFn: async () => {
			const response = await apiClient.get("/proving-ground/scenarios?limit=100&offset=0");
			return response.data.data;
		},
	});
}

export function useSaveScenario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, ...body }: Partial<Scenario> & { id?: string }) => {
			const response = id
				? await apiClient.put(`/proving-ground/scenarios/${id}`, body)
				: await apiClient.post("/proving-ground/scenarios", body);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.scenarios() });
			toast.success("Scenario saved");
		},
		onError: (error) => reportError(error, "Could not save the scenario"),
	});
}

export function useDeleteScenario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => await apiClient.delete(`/proving-ground/scenarios/${id}`).then((response) => response.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.scenarios() });
			toast.success("Scenario deleted");
		},
		onError: (error) => reportError(error, "Could not delete the scenario"),
	});
}

/* ------------------------------------------------------------------ metrics */

export function useMetrics() {
	return useQuery<Metric[]>({
		queryKey: provingGroundKeys.metrics(),
		queryFn: async () => {
			const response = await apiClient.get("/proving-ground/metrics?limit=100&offset=0");
			return response.data.data;
		},
	});
}

export function useSaveMetric() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, ...body }: Partial<Metric> & { id?: string }) => {
			const response = id
				? await apiClient.put(`/proving-ground/metrics/${id}`, body)
				: await apiClient.post("/proving-ground/metrics", body);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.metrics() });
			toast.success("Metric saved");
		},
		onError: (error) => reportError(error, "Could not save the metric"),
	});
}

export function useDeleteMetric() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => await apiClient.delete(`/proving-ground/metrics/${id}`).then((response) => response.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.metrics() });
			toast.success("Metric deleted");
		},
		onError: (error) => reportError(error, "Could not delete the metric"),
	});
}

/* --------------------------------------------------------------------- runs */

/**
 * Polls while a run is in flight. A voice grid takes a quarter of an hour, and a page that
 * only updates on reload is indistinguishable from one that has hung.
 */
export function useRun(id?: number) {
	return useQuery<RunDetail>({
		queryKey: provingGroundKeys.run(id ?? 0),
		queryFn: async () => await apiClient.get(`/proving-ground/runs/${id}`).then((response) => response.data),
		enabled: Boolean(id),
		refetchInterval: (query) => {
			const run = query.state.data;
			if (!run) return false;
			if (run.status === "queued" || run.status === "running") return 3000;
			// Suggestions are written a few seconds after the run is marked complete, so polling
			// that stops at "completed" stops one step too early and the panel stays empty until
			// somebody reloads. Keep going until they land — or until it is clear none are coming.
			// Only a reader who receives the advice waits for it. `suggestionsAt` is null for a
			// manager too, so polling on that alone refetched every three seconds for ever on a
			// page whose payload will never change.
			const failed = run.totalChecks - run.passedChecks;
			const awaitingAdvice =
				run.status === "completed" && failed > 0 && run.suggestions !== undefined && !run.suggestionsAt;
			if (!awaitingAdvice) return false;
			// Bounded: the advisor swallows its own errors, so without a ceiling a job that died
			// leaves the tab polling until it is closed.
			const finished = run.finishedAt ? Date.parse(run.finishedAt) : Date.now();
			return Date.now() - finished < 5 * 60_000 ? 3000 : false;
		},
	});
}

export function useRunCell(id?: number) {
	return useQuery<CellDetail>({
		queryKey: provingGroundKeys.cell(id ?? 0),
		queryFn: async () => await apiClient.get(`/proving-ground/runs/cells/${id}`).then((response) => response.data),
		enabled: Boolean(id),
	});
}

export function useLaunchRun() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: { agentId: string; mode: RunMode; scenarioIds: string[]; personaIds: string[] }) =>
			await apiClient.post("/proving-ground/runs", body).then((response) => response.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.runs() });
			toast.success("Run started");
		},
		onError: (error) => reportError(error, "Could not start the run"),
	});
}

export function useCancelRun() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => await apiClient.post(`/proving-ground/runs/${id}/cancel`, {}).then((response) => response.data),
		onSuccess: (_, id) => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.run(id) });
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.runs() });
			toast.success("Run cancelled");
		},
		onError: (error) => reportError(error, "Could not cancel the run"),
	});
}

/**
 * Records a person's verdict beside the judge's. Passing null clears it.
 */
export function useOverrideVerdict(cellId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ verdictId, passed, note }: { verdictId: number; passed: boolean | null; note?: string }) =>
			await apiClient.put(`/proving-ground/runs/verdicts/${verdictId}`, { passed, note }).then((response) => response.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.cell(cellId) });
		},
		onError: (error) => reportError(error, "Could not save your verdict"),
	});
}

/* ----------------------------------------------------------------- settings */

export function useProvingGroundSettings() {
	return useQuery<ProvingGroundSettings>({
		queryKey: provingGroundKeys.settings(),
		queryFn: async () => await apiClient.get("/proving-ground/settings").then((response) => response.data),
	});
}

export function useSaveProvingGroundSettings() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: Partial<ProvingGroundSettings>) =>
			await apiClient.put("/proving-ground/settings", body).then((response) => response.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: provingGroundKeys.settings() });
			toast.success("Settings saved");
		},
		onError: (error) => reportError(error, "Could not save settings"),
	});
}
